import maplibregl from 'maplibre-gl';
import type { HeatPoint, DangerZone, RuleKey } from './heatmap-types';
import { RULE_KEY_ORDER, RULE_VISUALS } from './heatmap-types';

export const DARK_TILE_STYLE  = 'https://tiles.openfreemap.org/styles/dark';
export const LIGHT_TILE_STYLE = 'https://tiles.openfreemap.org/styles/positron';

let _markers: maplibregl.Marker[] = [];
let _is3D = false;
let _currentStyle = DARK_TILE_STYLE;

function currentTileStyle(): string {
  return document.documentElement.getAttribute('data-theme') === 'light'
    ? LIGHT_TILE_STYLE
    : DARK_TILE_STYLE;
}

export function initMap(containerId: string): maplibregl.Map {
  _currentStyle = currentTileStyle();
  const map = new maplibregl.Map({
    container: containerId,
    style: _currentStyle,
    center: [0, 20],
    zoom: 2,
  });
  map.addControl(new maplibregl.NavigationControl(), 'top-right');
  map.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left');
  // Suppress internal tile errors (e.g. out-of-range DEM tiles) from propagating
  map.on('error', (e) => {
    const msg = (e as { error?: { message?: string } }).error?.message ?? '';
    if (msg.includes('DEM') || msg.includes('terrain')) return;
    console.warn('[heatmap-map] map error:', msg);
  });
  return map;
}

export function setupLayers(map: maplibregl.Map): void {
  // One heat layer per rule
  for (const key of RULE_KEY_ORDER) {
    const v = RULE_VISUALS[key];
    map.addSource(`source-${key}`, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
    map.addLayer({
      id: `layer-${key}`,
      type: 'heatmap',
      source: `source-${key}`,
      paint: {
        'heatmap-weight': ['interpolate', ['linear'], ['get', 'intensity'], 0, 0, 1, 1],
        'heatmap-intensity': 1.0,
        'heatmap-color': [
          'interpolate', ['linear'], ['heatmap-density'],
          0,   'rgba(0,0,0,0)',
          0.4, v.heatColors[1],
          1,   v.heatColors[2],
        ],
        'heatmap-radius': 30,
        'heatmap-opacity': 0.85,
      },
    });
  }
}

export function setHeatPoints(map: maplibregl.Map, points: HeatPoint[]): void {
  const byRule = new Map<RuleKey, HeatPoint[]>();
  for (const key of RULE_KEY_ORDER) byRule.set(key, []);
  for (const p of points) byRule.get(p.ruleKey)!.push(p);

  for (const key of RULE_KEY_ORDER) {
    const src = map.getSource(`source-${key}`) as maplibregl.GeoJSONSource | undefined;
    if (!src) continue;
    src.setData({
      type: 'FeatureCollection',
      features: byRule.get(key)!.map(p => ({
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [p.longitude, p.latitude] },
        properties: { intensity: p.intensity },
      })),
    });
  }
}

export function setLayerVisible(map: maplibregl.Map, ruleKey: RuleKey, visible: boolean): void {
  if (!map.getLayer(`layer-${ruleKey}`)) return;
  map.setLayoutProperty(`layer-${ruleKey}`, 'visibility', visible ? 'visible' : 'none');
}

export function setHeatRadius(map: maplibregl.Map, radius: number): void {
  for (const key of RULE_KEY_ORDER) {
    if (map.getLayer(`layer-${key}`)) {
      map.setPaintProperty(`layer-${key}`, 'heatmap-radius', radius);
    }
  }
}

export function setHeatIntensity(map: maplibregl.Map, ruleKey: RuleKey, intensity: number): void {
  if (map.getLayer(`layer-${ruleKey}`)) {
    map.setPaintProperty(`layer-${ruleKey}`, 'heatmap-intensity', intensity);
  }
}

export function addDangerZoneMarkers(
  map: maplibregl.Map,
  zones: DangerZone[],
  onZoneClick: (zone: DangerZone) => void,
): void {
  _markers.forEach(m => m.remove());
  _markers = [];

  for (const zone of zones) {
    const color = RULE_VISUALS[zone.dominantRule].color;
    const el = document.createElement('div');
    el.className = 'hm-dz-marker';
    el.style.cssText = `
      width:28px;height:28px;border-radius:50%;
      background:${color}22;border:2px solid ${color};
      color:${color};font-weight:700;font-size:11px;
      display:flex;align-items:center;justify-content:center;
      cursor:pointer;backdrop-filter:blur(4px);
      box-shadow:0 0 12px ${color}55;
      font-family:var(--mono,monospace);
    `;
    el.textContent = String(zone.rank);
    el.title = `#${zone.rank} — ${zone.totalViolations} violations`;
    el.addEventListener('click', e => { e.stopPropagation(); onZoneClick(zone); });

    const marker = new maplibregl.Marker({ element: el })
      .setLngLat([zone.centroidLng, zone.centroidLat])
      .addTo(map);
    _markers.push(marker);
  }
}

export function flyToBounds(map: maplibregl.Map, points: HeatPoint[]): void {
  if (points.length === 0) return;
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
  for (const p of points) {
    if (p.latitude  < minLat) minLat = p.latitude;
    if (p.latitude  > maxLat) maxLat = p.latitude;
    if (p.longitude < minLng) minLng = p.longitude;
    if (p.longitude > maxLng) maxLng = p.longitude;
  }
  map.fitBounds(
    [[minLng, minLat], [maxLng, maxLat]],
    { padding: 60, duration: 1000 },
  );
}

// ── 3D mode: pitch + buildings only (no DEM terrain to avoid tile errors) ──

export function set3DMode(map: maplibregl.Map, enabled: boolean): void {
  _is3D = enabled;
  const isLight = document.documentElement.getAttribute('data-theme') === 'light';

  if (enabled) {
    if (!map.getLayer('layer-buildings-3d')) {
      const buildingLayer = map.getStyle().layers?.find(
        l => l.type === 'fill' && (l as maplibregl.FillLayerSpecification)['source-layer'] === 'building'
      );
      try {
        map.addLayer({
          id: 'layer-buildings-3d',
          type: 'fill-extrusion',
          source: 'openmaptiles',
          'source-layer': 'building',
          paint: {
            'fill-extrusion-color': isLight ? '#c8d6ea' : '#1a2030',
            'fill-extrusion-height': ['coalesce', ['get', 'render_height'], ['get', 'height'], 10],
            'fill-extrusion-base':   ['coalesce', ['get', 'render_min_height'], ['get', 'min_height'], 0],
            'fill-extrusion-opacity': isLight ? 0.5 : 0.7,
          },
        }, buildingLayer?.id);
      } catch {
        // Building source not available at this zoom level
      }
    } else {
      map.setLayoutProperty('layer-buildings-3d', 'visibility', 'visible');
    }
    map.easeTo({ pitch: 50, bearing: -15, duration: 700 });
  } else {
    if (map.getLayer('layer-buildings-3d')) {
      map.setLayoutProperty('layer-buildings-3d', 'visibility', 'none');
    }
    map.easeTo({ pitch: 0, bearing: 0, duration: 700 });
  }
}

export function setMapTheme(
  map: maplibregl.Map,
  isLight: boolean,
  onReady: (map: maplibregl.Map) => void,
): void {
  const newStyle = isLight ? LIGHT_TILE_STYLE : DARK_TILE_STYLE;
  if (_currentStyle === newStyle) return;
  _currentStyle = newStyle;
  _is3D = false; // 3D layer is removed when style reloads
  map.setStyle(newStyle, { diff: false } as Parameters<typeof map.setStyle>[1]);
  map.once('style.load', () => onReady(map));
}

export function is3DActive(): boolean {
  return _is3D;
}

// ── Cleanup ───────────────────────────────────────────────────────────────

export function resetMapState(): void {
  _markers.forEach(m => { try { m.remove(); } catch { /* already removed */ } });
  _markers = [];
  _is3D = false;
}
